"use client";

import { useEffect, useState } from 'react';
import { DownloadIcon } from '@/components/common/button/DownloadIcon';
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from '@/components/ui/table';
import type { AttachmentListTableItem } from '../static';

type AttachmentListTableSectionProps = {
  heading?: string;
  items: AttachmentListTableItem[];
  show_category_column?: boolean;
  show_year_column?: boolean;
  show_file_size_column?: boolean;
  striped?: boolean;
  bordered?: boolean;
  add_background?: boolean;
  add_bottom_border?: boolean;
};

export default function AttachmentListTableSection({
  heading,
  items,
  show_category_column = false,
  show_year_column = false,
  show_file_size_column = false,
  striped = true,
  add_background = false,
  add_bottom_border = false,
}: AttachmentListTableSectionProps) {
  const ITEMS_PER_PAGE = 10;
  const totalPages = Math.max(1, Math.ceil(items.length / ITEMS_PER_PAGE));
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    setCurrentPage(1);
  }, [items]);

  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedItems = items.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  const content = (
    <div className={add_bottom_border ? 'pb-8 border-b border-gray-200' : ''}>
      {heading && (
        <h2 className="text-3xl md:text-4xl font-bold text-primary-700 mb-6">
          {heading}
        </h2>
      )}
      <div className="overflow-x-auto w-full">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Title</TableHead>
              {show_category_column && <TableHead>Category</TableHead>}
              {show_year_column && <TableHead>Year</TableHead>}
              {show_file_size_column && <TableHead>Size</TableHead>}
              <TableHead className="text-center">Download</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {paginatedItems.map((item, idx) => (
              <TableRow
                key={`${startIndex + idx}-${item.fileUrl}`}
                className={striped && (startIndex + idx) % 2 !== 0 ? 'bg-gray-50' : ''}
              >
                <TableCell className="whitespace-normal break-words min-w-[200px]">
                  <p className="font-medium text-gray-800">{item.title}</p>
                  {item.description && (
                    <p className="text-xs text-gray-500 mt-0.5">{item.description}</p>
                  )}
                </TableCell>
                {show_category_column && (
                  <TableCell className="text-gray-600 whitespace-nowrap">
                    {item.category ?? '—'}
                  </TableCell>
                )}
                {show_year_column && (
                  <TableCell className="text-gray-600 whitespace-nowrap">
                    {item.year ?? '—'}
                  </TableCell>
                )}
                {show_file_size_column && (
                  <TableCell className="text-gray-500 whitespace-nowrap">
                    {item.fileSize ?? '—'}
                  </TableCell>
                )}
                <TableCell className="text-center">
                  <a
                    href={item.fileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    download
                    className="inline-flex items-center justify-center"
                  >
                    <DownloadIcon />
                  </a>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {items.length > ITEMS_PER_PAGE && (
        <div className="flex items-center gap-3 pt-4">
          <button
            type="button"
            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
            disabled={currentPage <= 1}
            className="px-3 py-1.5 text-sm border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          <span className="text-sm text-gray-600">
            Page {currentPage} of {totalPages}
          </span>
          <button
            type="button"
            onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
            disabled={currentPage >= totalPages}
            className="px-3 py-1.5 text-sm border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );

  if (add_background) {
    return (
      <section className="w-screen relative left-1/2 right-1/2 -mx-[50vw] bg-gradient-to-b from-[rgba(237,237,249,0)] to-[#DEE5E8] py-12">
        <div
          className="absolute left-0 bottom-0 w-[300px] h-[300px] bg-no-repeat bg-left-bottom bg-contain pointer-events-none"
          style={{ backgroundImage: "url(/images/left-corner-supergrafis.png)" }}
        />
        <div className="container max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
          {content}
        </div>
      </section>
    );
  }

  return <div className="w-full">{content}</div>;
}
